'use client';

import { useEffect, useRef } from 'react';

import type { PostMediaItem } from '@ps/api/types';

import { BlankedMedia } from './BlankedMedia';

interface Props {
  media: PostMediaItem;
  active: boolean;
  focused: boolean;
  alt?: string;
  className?: string;
}

/**
 * VIDEO branch — native `<video controls playsInline preload="metadata" poster>`.
 * `src` prefers `media.processedUrl` (browser-friendly transcoded MP4) over the
 * raw upload. `controlsList="nodownload"` is set imperatively after mount (same
 * hardening legacy does at `slide-view.tsx:414-422`). A restricted-and-blanked
 * slot renders the {@link BlankedMedia} blurhash preview instead of an empty
 * `<video>`.
 *
 * Auto-pause: driven by `active` + `focused`, the `<video>` pauses when the slide
 * leaves the viewport or the tab blurs, and resumes from the saved position on
 * re-entry — but only if the user had pressed play. Direct DOM calls (RxJS-free).
 */
export function PostVideoMedia({
  media,
  active,
  focused,
  alt,
  className,
}: Props) {
  const ref = useRef<HTMLVideoElement>(null);
  // Only auto-resume on re-entry if the user had been playing before the slide /
  // tab lost focus — mirrors legacy `shouldResumePlaying` (`slide-view.tsx:375`).
  const wasPlayingRef = useRef(false);
  const canPlay = active && focused;

  // Disable the "download" item + PiP in the native controls menu.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.setAttribute('controlsList', 'nodownload');
    el.setAttribute('disablePictureInPicture', '');
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!canPlay) {
      // Leaving the viewport / tab — remember whether it was playing, then pause.
      wasPlayingRef.current = !el.paused;
      if (!el.paused) {
        el.pause();
      }
    } else if (wasPlayingRef.current) {
      // Re-entered AND was playing before — resume. `play()` rejects in Safari
      // when the gesture lineage is lost; swallow it to avoid unhandled rejection.
      void el.play().catch(() => undefined);
    }
  }, [canPlay]);

  const src = media.processedUrl ?? media.url;
  if (!src) {
    return (
      <BlankedMedia blurhash={media.metadata?.blurhash} className={className} />
    );
  }

  return (
    <video
      ref={ref}
      // Enough to get duration + poster without pulling the whole file.
      preload="metadata"
      controls
      playsInline
      poster={media.thumbnailUrl}
      src={src}
      onError={event => {
        // Surface a 404 / CORS / codec rejection instead of a silently-blank video.
        console.error('[PostVideoMedia] video load failed', {
          src,
          type: media.type,
          contentType: media.contentType,
          error: event.currentTarget.error,
        });
      }}
      aria-label={alt ?? media.caption ?? 'Post video'}
      // Absolute-fill so the carousel's shared aspect ratio drives the size.
      className={`absolute inset-0 h-full w-full ${className ?? ''}`}
    >
      Your browser doesn&apos;t support inline video.
    </video>
  );
}
